import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, Utensils, ShoppingBag, Wrench, Compass, MapPin, Star, Truck, Car, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATS = [
  { key: "food", label: "Alimentation", Icon: Utensils },
  { key: "shopping", label: "Shopping", Icon: ShoppingBag },
  { key: "services", label: "Services", Icon: Wrench },
  { key: "todo", label: "À faire", Icon: Compass },
];

const PlaceRow = ({ p, onPick }) => (
  <button onClick={() => onPick(p)} data-testid={`explore-place-${p.id}`}
    className="w-full flex items-center gap-3 py-3 px-2 border-b border-white/5 text-left hover:bg-white/5 transition-colors rounded-lg">
    <div className="w-10 h-10 rounded-full bg-[#D4AF37]/15 flex items-center justify-center shrink-0">
      <MapPin className="w-5 h-5 text-[#D4AF37]" />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-white font-semibold truncate">{p.name}</p>
      <p className="text-xs text-slate-400 truncate">{p.address}</p>
    </div>
    {p.rating && <span className="text-xs text-amber-300 flex items-center gap-1"><Star className="w-3 h-3 fill-amber-300" />{p.rating}</span>}
  </button>
);

const PickedActions = ({ place, onGo }) => (
  <div className="mt-3 p-4 rounded-2xl bg-[#003594]/20 border border-[#003594]/50" data-testid="explore-picked">
    <p className="text-white font-black">{place.name}</p>
    <p className="text-xs text-emerald-400 mb-3">Enregistré dans vos lieux WÔMA</p>
    <div className="grid grid-cols-2 gap-2">
      <button onClick={() => onGo("delivery")} data-testid="explore-go-delivery"
        className="flex items-center justify-center gap-2 bg-[#D4AF37] text-black rounded-full py-2.5 text-sm font-black">
        <Truck className="w-4 h-4" /> Livraison
      </button>
      <button onClick={() => onGo("taxi")} data-testid="explore-go-taxi"
        className="flex items-center justify-center gap-2 bg-white text-black rounded-full py-2.5 text-sm font-black">
        <Car className="w-4 h-4" /> Taxi
      </button>
    </div>
  </div>
);

export default function ExploreDakarPanel({ pos, onClose, onSaved }) {
  const navigate = useNavigate();
  const [cat, setCat] = useState("food");
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(null);

  const la = pos[0].toFixed(2);
  const lo = pos[1].toFixed(2);
  useEffect(() => {
    let stale = false;
    setLoading(true);
    setPicked(null);
    axios.get(`${API}/explore/places`, { params: { category: cat, lat: la, lng: lo } })
      .then((r) => !stale && setPlaces(r.data.places || []))
      .catch((e) => !stale && (setPlaces([]), toast.error(e.response?.data?.detail || "Impossible de charger les lieux")))
      .finally(() => !stale && setLoading(false));
    return () => { stale = true; };
  }, [cat, la, lo]);

  const pick = async (p) => {
    setPicked(p);
    try {
      await axios.post(`${API}/users/me/saved-places`, { name: p.name, address: p.address, lat: p.lat, lng: p.lng, category: cat, source: p.source });
      onSaved?.();
    } catch {
      toast.error("Connectez-vous pour enregistrer ce lieu");
    }
  };

  const go = (kind) => {
    const address = [picked.name, picked.address].filter(Boolean).join(", ");
    const q = kind === "taxi"
      ? new URLSearchParams({ dest_lat: picked.lat, dest_lng: picked.lng, dest_address: address })
      : new URLSearchParams({ delivery_lat: picked.lat, delivery_lng: picked.lng, delivery_address: address });
    navigate(`${kind === "taxi" ? "/taxi" : "/orders/new"}?${q.toString()}`);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center" onClick={onClose} data-testid="explore-dakar-panel">
      <div className="w-full sm:max-w-lg max-h-[85vh] bg-[#111827] rounded-t-3xl sm:rounded-3xl p-5 flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-black text-[#D4AF37] italic" style={{ fontFamily: "Outfit, sans-serif" }}>Explorer Dakar</h2>
          <button onClick={onClose} data-testid="explore-close" className="w-9 h-9 rounded-full hover:bg-white/10 text-white flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex gap-2 overflow-x-auto sm:flex-wrap pb-2 shrink-0">
          {CATS.map(({ key, label, Icon }) => (
            <button key={key} onClick={() => setCat(key)} data-testid={`explore-cat-${key}`}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-colors ${cat === key ? "bg-[#D4AF37] text-black" : "bg-white/10 text-white hover:bg-white/15"}`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>
        {picked && <PickedActions place={picked} onGo={go} />}
        <div className="mt-2 overflow-y-auto flex-1" data-testid="explore-results">
          {loading && <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" /></div>}
          {!loading && places.length === 0 && <p className="py-10 text-center text-slate-400 text-sm">Aucun lieu trouvé autour de vous</p>}
          {!loading && places.map((p) => <PlaceRow key={p.id} p={p} onPick={pick} />)}
        </div>
      </div>
    </div>
  );
}
